/**
 * Cloudflare Image Transformations helper for the admin panel.
 *
 * All public book covers (and any other public image) live on R2 and
 * are served through `images.unplayist.com`, which is a Cloudflare
 * custom domain on the unplayist.com zone. Image Transformations is
 * enabled on the zone, so any path can be resized on the fly via
 *
 *     https://images.unplayist.com/cdn-cgi/image/<options>/<filename>
 *
 * Keep the preset catalogue tiny so the edge cache stays warm across
 * users — adding ad-hoc widths inflates the cardinality and burns
 * the monthly transformation quota on first-paint requests.
 *
 * This module is intentionally a copy of the website's
 * `frontend-website/src/lib/images/presets.ts`. We don't share
 * source today because the two frontends use different bundlers
 * (Vite vs Next.js); when a real shared-package step lands in the
 * monorepo this can move into it.
 */

export type ImagePreset = "thumb" | "card" | "detail" | "hero" | "og" | "blur";

const PRESET_OPTS: Record<ImagePreset, string> = {
  thumb: "width=200,quality=80,format=auto,fit=cover",
  card: "width=400,quality=82,format=auto,fit=cover",
  detail: "width=800,quality=85,format=auto,fit=cover",
  hero: "width=1600,quality=85,format=auto,fit=cover",
  og: "width=1200,height=630,fit=cover,format=jpeg,quality=82",
  blur: "width=20,blur=30,quality=40,format=auto",
};

function baseHost(): string {
  // Vite exposes env at build time on `import.meta.env`. The panel
  // is always built against the production host, but we still allow
  // overriding for one-off staging deployments.
  const v = (import.meta as unknown as { env?: Record<string, string> }).env
    ?.VITE_IMAGE_CDN_HOST;
  return (typeof v === "string" && v.trim()) || "images.unplayist.com";
}

/**
 * Build a Cloudflare-transformed image URL. Returns `undefined` for
 * empty/null filenames so React `<img src={...}>` collapses gracefully.
 */
export function imageUrl(
  filename: string | null | undefined,
  preset: ImagePreset,
  opts: { dpr?: 1 | 2 | 3 } = {},
): string | undefined {
  if (!filename) return undefined;

  const presetOpts = PRESET_OPTS[preset];
  const dpr = opts.dpr ?? 1;
  const optString = dpr > 1 ? `${presetOpts},dpr=${dpr}` : presetOpts;
  const host = baseHost();

  if (/^https?:\/\//i.test(filename)) {
    return `https://${host}/cdn-cgi/image/${optString}/${filename}`;
  }
  const clean = filename.replace(/^\/+/, "");
  return `https://${host}/cdn-cgi/image/${optString}/${clean}`;
}

/**
 * 1x + 2x srcSet pair for plain `<img>` tags that need retina support
 * without involving Next/Image.
 */
export function imageSrcSet(
  filename: string | null | undefined,
  preset: ImagePreset,
): { src: string; srcSet: string } | undefined {
  const x1 = imageUrl(filename, preset, { dpr: 1 });
  const x2 = imageUrl(filename, preset, { dpr: 2 });
  if (!x1 || !x2) return undefined;
  return { src: x1, srcSet: `${x1} 1x, ${x2} 2x` };
}
